"use client";

import * as React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";

// Extract options type from useEmblaCarousel parameters (avoids extra package dependency)
type EmblaOptionsType = NonNullable<Parameters<typeof useEmblaCarousel>[0]>;
import { useCarouselScroll, useCarouselIndex } from "./use-carousel-state";
import { CarouselArrows } from "./CarouselArrows";
import { CarouselDots, PaginationCounter } from "./CarouselDots";
import type { CarouselApi, CarouselBreakpoint } from "./types";

/**
 * Default RTL options for Embla
 */
const DEFAULT_RTL_OPTIONS: Partial<EmblaOptionsType> = {
  direction: "rtl",
  align: "start",
  containScroll: "trimSnaps",
};

export interface EmblaCarouselProps {
  children: React.ReactNode;
  /** Embla carousel options */
  options?: EmblaOptionsType;
  /** Custom class for outer container */
  className?: string;
  /** Custom class for viewport/overflow container */
  viewportClassName?: string;
  /** Custom class for slides container */
  containerClassName?: string;
  /** Gap between slides in pixels */
  gap?: number;
  /** Callback when API is ready */
  onApiReady?: (api: CarouselApi) => void;
  /** Show navigation arrows */
  showArrows?: boolean;
  /** Custom arrows class */
  arrowsClassName?: string;
  /** Show pagination dots */
  showDots?: boolean;
  /** Custom dots class */
  dotsClassName?: string;
  /** Dots variant */
  dotsVariant?: "default" | "line" | "pill";
  /** Show pagination counter */
  showCounter?: boolean;
  /** Counter class */
  counterClassName?: string;
  /** Responsive breakpoints for slides per view */
  breakpoints?: CarouselBreakpoint[];
  /** Slides per view (for non-responsive) */
  slidesPerView?: number;
}

/**
 * Core Embla Carousel wrapper with RTL support
 * Provides a base for all carousel implementations
 */
export function EmblaCarousel({
  children,
  options = {},
  className,
  viewportClassName,
  containerClassName,
  gap = 16,
  onApiReady,
  showArrows = false,
  arrowsClassName,
  showDots = false,
  dotsClassName,
  dotsVariant = "default",
  showCounter = false,
  counterClassName,
}: EmblaCarouselProps) {
  const mergedOptions = React.useMemo(
    () => ({ ...DEFAULT_RTL_OPTIONS, ...options }),
    [options]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(mergedOptions);

  const { canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useCarouselScroll(emblaApi);
  const { selectedIndex, totalSlides, scrollTo } = useCarouselIndex(emblaApi);

  // Notify parent when API is ready
  React.useEffect(() => {
    if (emblaApi && onApiReady) {
      onApiReady(emblaApi);
    }
  }, [emblaApi, onApiReady]);

  return (
    <div className={cn("relative", className)}>
      {/* Viewport with overflow hidden */}
      <div ref={emblaRef} className={cn("overflow-hidden", viewportClassName)}>
        {/* Slides container */}
        <div
          className={cn("flex", containerClassName)}
          style={{ gap: `${gap}px` }}
        >
          {children}
        </div>
      </div>

      {/* Navigation arrows */}
      {showArrows && (
        <CarouselArrows
          className={arrowsClassName}
          canScrollPrev={canScrollPrev}
          canScrollNext={canScrollNext}
          onPrevClick={scrollPrev}
          onNextClick={scrollNext}
        />
      )}

      {/* Pagination dots */}
      {showDots && totalSlides > 1 && (
        <CarouselDots
          total={totalSlides}
          current={selectedIndex}
          onDotClick={scrollTo}
          className={dotsClassName}
          variant={dotsVariant}
        />
      )}

      {/* Counter */}
      {showCounter && (
        <PaginationCounter
          current={selectedIndex + 1}
          total={totalSlides}
          className={counterClassName}
        />
      )}
    </div>
  );
}

/**
 * Carousel slide wrapper component
 */
export interface EmblaSlideProps {
  children: React.ReactNode;
  className?: string;
  /** Flex basis for slide width (e.g., "100%", "50%", "calc(33.333% - 16px)") */
  basis?: string;
  onClick?: () => void;
}

export function EmblaSlide({
  children,
  className,
  basis = "100%",
  onClick,
}: EmblaSlideProps) {
  return (
    <div
      className={cn("min-w-0 shrink-0 grow-0", className)}
      style={{ flexBasis: basis }}
      role="group"
      aria-roledescription="slide"
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/**
 * Hook to calculate slide basis based on breakpoints
 */
export function useResponsiveBasis(
  breakpoints: CarouselBreakpoint[],
  defaultSlidesPerView: number = 1,
  gap: number = 16
): string {
  const [basis, setBasis] = React.useState<string>(
    calculateBasis(defaultSlidesPerView, gap)
  );

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let slidesPerView = defaultSlidesPerView;
      let currentGap = gap;

      // Find matching breakpoint (sorted by minWidth descending)
      const sortedBreakpoints = [...breakpoints].sort(
        (a, b) => b.minWidth - a.minWidth
      );
      for (const bp of sortedBreakpoints) {
        if (width >= bp.minWidth) {
          slidesPerView = bp.slidesPerView;
          currentGap = bp.gap ?? gap;
          break;
        }
      }

      setBasis(calculateBasis(slidesPerView, currentGap));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoints, defaultSlidesPerView, gap]);

  return basis;
}

function calculateBasis(slidesPerView: number, gap: number): string {
  // Handle fractional slides (e.g., 2.5 slides visible)
  const isWholeNumber = Number.isInteger(slidesPerView);

  if (isWholeNumber) {
    if (slidesPerView === 1) return "100%";
    return `calc(${100 / slidesPerView}% - ${
      (gap * (slidesPerView - 1)) / slidesPerView
    }px)`;
  }

  // For fractional, calculate based on the fractional part
  return `calc(${100 / slidesPerView}% - ${
    gap * ((slidesPerView - 1) / slidesPerView)
  }px)`;
}
